import { Contentful } from '../../types/contentful'
import { isObject, path } from '../../utils/obj'
import { currencyFormat, percentageFormat } from '../../utils/formatter'
import { getArrayValue } from '../../utils/product-schema'

/**
 * The type of the value, either an absolute value 'value', or a reference value 'valueFrom'.
 *
 * @typedef {string|null} ValueType
 */

/**
 * The value, either an absolute value, or a reference value.
 *
 * @typedef {*} Value
 */

/**
 * Takes an object slice from the schema, and finds whether the slice is a 'value' or 'valueFrom'
 * type.
 *
 * @param {Object} slice
 * @param {*] [slice.value] - The absolute value.
 * @param {string} [slice.valueFrom] - The path to the value on the dataset.
 * @returns {[ValueType, Value]}
 */

const getValueType = (
  slice: Contentful.ValueFromType,
): [string | null, Contentful.GetValueResultType] => {
  let result: [string | null, Contentful.GetValueResultType] = [null, null]
  if (!isObject(slice)) {
    return result
  }

  if ({}.hasOwnProperty.call(slice, 'valueFrom')) {
    result = ['valueFrom', slice.valueFrom!]
  } else if ({}.hasOwnProperty.call(slice, 'value')) {
    result = ['value', slice.value!]
  } else if ({}.hasOwnProperty.call(slice, 'valueCalculated')) {
    result = ['valueCalculated', slice.valueCalculated!]
  } else if ({}.hasOwnProperty.call(slice, 'compound')) {
    result = ['compound', JSON.stringify(slice.compound)]
  } else if ({}.hasOwnProperty.call(slice, 'hierarchy')) {
    result = ['hierarchy', JSON.stringify(slice.hierarchy)]
  } else if ({}.hasOwnProperty.call(slice, 'custom')) {
    result = ['custom', JSON.stringify(slice.custom)]
  }

  return result
}

/**
 * Takes an object slice from the schema, along with the dataset, and returns the absolute value,
 * either from the dataset or the schema.
 *
 * @param {Object} slice
 * @param {*] [slice.value] - The absolute value.
 * @param {string} [slice.valueFrom] - The path to the value on the dataset.
 * @param {Object} dataset - The dataset to lookup.
 * @returns {[Value, ValueType]}
 */
const getValue = (slice: any, dataset?: any) => {
  const [type, value] = getValueType(slice)
  if (type === 'valueFrom') {
    return path((value as string).split('.'), dataset)
  }

  return value
}

export const getField = (slice: Contentful.ValueFromType) => getValueType(slice)[1]

interface ListObject {
  label: Object
  value: Object
}

const getListValues = (product: Object) => (content: ListObject) => {
  return {
    label: getValue(content.label, product),
    value: getValue(content.value, product),
  }
}

const getTextValues = (product: Object) => (content: Object) => getValue(content, product)

interface TableCol {
  formatter: string
  value: any
}

const applyFormat = (col: TableCol) => {
  const format = {
    percentage: (v: number) => percentageFormat(v),
    currency: (v: number) => currencyFormat(v),
  }

  return {
    ...col,
    value: (format[col.formatter] || ((v: number) => v))(col.value),
  }
}

interface Addon {
  content: any[]
  view: string
}

export const getMoreInformationContent = (addon: Addon, product: Object) => {
  let addonContent = []

  if (addon.view === 'list') {
    addonContent = addon.content
      .map(getListValues(product))
      .filter(({ value }) => value !== null && value !== false)
  } else if (addon.view === 'table') {
    const content: any[] = []

    for (const tableRow of addon.content) {
      if (tableRow.field) {
        const fields = getArrayValue(tableRow.field, product)

        if (fields) {
          for (const field of fields) {
            const row = tableRow.fieldSchema.map((col: TableCol) => {
              return { ...col, value: field[getValue(col.value, product)] }
            })

            content.push(row)
          }
        } else {
          return null
        }
      } else {
        const row = tableRow.data.map((col: TableCol) => {
          return { ...col, value: getValue(col.value, product) }
        })

        content.push(row)
      }
    }

    addonContent = content.map((row) => row.map(applyFormat))
  } else if (addon.view === 'HTML') {
    addonContent = addon.content.map(getTextValues(product))
  }

  if (!addonContent || addonContent?.length === 0 || addonContent.every((chunk) => !chunk)) {
    return null
  }

  return addonContent
}

export const createOpsgenieError = (contentfulId: string, message: string, tags: Array<string>) => {
  fetch('https://www.money.co.uk/opsgenie/error', {
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      message: message,
      tags: tags,
      alias: contentfulId,
    }),
  }).catch((e) => {
    /* eslint-disable no-console */
    console.error('Failed to post to OpsGenie:', e)
  })
}
